export interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

export interface ProcessContent {
  eyebrow: string;
  headline: string;
  steps: ProcessStep[];
}

interface Props {
  content: ProcessContent;
}

const ProcessSection = ({ content }: Props) => {
  const { eyebrow, headline, steps } = content;

  return (
    <section className="relative py-40 overflow-hidden" style={{ background: 'linear-gradient(160deg, #0D1B2A 0%, #122234 55%, #0F1E2E 100%)' }}>

      {/* Subtle ghost text */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-serif font-bold text-white/[0.018] select-none pointer-events-none leading-none"
        style={{ fontSize: 'clamp(18rem, 36vw, 46rem)' }}
        aria-hidden="true"
      >
        PRO
      </div>

      {/* Gold accent lines */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(201,168,76,0.4) 30%, rgba(201,168,76,0.4) 70%, transparent 100%)' }} />
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(26,74,82,0.4) 30%, rgba(26,74,82,0.4) 70%, transparent 100%)' }} />

      <div className="relative z-10 max-w-[1280px] mx-auto px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-24 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-10 bg-gold" />
              <span className="text-gold text-[11px] tracking-[0.3em] uppercase font-sans font-semibold">
                {eyebrow}
              </span>
            </div>
            <h2 className="font-serif text-white font-bold leading-tight"
              style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)' }}>
              {headline}
            </h2>
            <div className="flex items-center gap-2.5 mt-6">
              <div className="h-[3px] w-16 bg-gold rounded-full" />
              <div className="h-[3px] w-5 rounded-full bg-gold/25" />
            </div>
          </div>
          <p className="text-white/75 text-sm font-sans leading-relaxed max-w-[320px] lg:text-right">
            Every job follows this proven process — from first contact to final guarantee.
          </p>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 relative">
          {/* Horizontal connector */}
          <div className="hidden lg:block absolute top-[44px] left-[12.5%] right-[12.5%] h-px z-0"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.3) 20%, rgba(201,168,76,0.3) 80%, transparent)' }} />

          {steps.map((step, idx) => (
            <div key={idx} className="relative z-10 group px-6 mb-14 lg:mb-0">
              {/* Step number circle */}
              <div className="relative w-[88px] h-[88px] flex items-center justify-center mb-9 mx-auto lg:mx-0">
                <div className="absolute inset-0 rounded-full border border-white/10 group-hover:border-teal/30 transition-all duration-500" />
                <div className="absolute inset-3 rounded-full transition-all duration-500"
                  style={{ background: 'rgba(26,74,82,0.2)', border: '1px solid rgba(26,74,82,0.35)' }} />
                <span className="font-serif text-teal-100 font-bold relative z-10"
                  style={{ fontSize: 'clamp(1.4rem, 2.5vw, 1.8rem)' }}>
                  {step.number}
                </span>
                {idx < steps.length - 1 && (
                  <div className="hidden lg:block absolute -right-[calc(50%-12px)] top-1/2 -translate-y-1/2 w-2 h-2 bg-teal/40 rounded-full z-20" />
                )}
              </div>

              {/* Step content */}
              <div className="text-center lg:text-left">
                <h3 className="font-serif text-white font-semibold text-xl mb-4 leading-snug group-hover:text-teal-100 transition-colors duration-300">
                  {step.title}
                </h3>
                <p className="text-white/75 text-[14px] leading-[1.85] font-sans max-w-[200px] mx-auto lg:mx-0">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-24 pt-14 border-t border-white/[0.07] flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex flex-wrap items-center gap-6">
            {['Licensed & Insured', '24/7 Available', 'Free Estimates', 'Guaranteed Work'].map(item => (
              <div key={item} className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-teal text-xs" />
                <span className="text-white/75 text-[11px] font-sans tracking-wide">{item}</span>
              </div>
            ))}
          </div>
          <a
            href="tel:+19090000000"
            className="flex items-center gap-2.5 text-white/75 hover:text-gold transition-colors cursor-pointer group whitespace-nowrap"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full border border-white/20 group-hover:border-gold/40 transition-colors flex-shrink-0">
              <i className="ri-phone-line text-xs" />
            </div>
            <span className="text-[12px] font-sans tracking-wide">Emergency Line — Always Available</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
